import { useEffect } from "react";
import { useHistory } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { memoryStore, useStore } from "../../lib/store";
import { useApiQuery } from "../../services/api";
import { useDebouncedValue } from "../../hooks/useDebouncedValue";
import { formatArticleDate } from "../../domains/app/utils/renderDate";
import { SEARCH_MIN_LENGTH } from "../../features/search/SearchPage";
import { useSearchHistory } from "../../features/search/hooks/useSearchHistory";
import { useRecordSettledSearch } from "../../features/search/hooks/useRecordSettledSearch";
import {
  useTrackSearchQuery,
  useTrackSearchSectionSettled,
} from "../../features/search/hooks/useTrackSearchQuery";
import { useCurrentUser } from "../../domains/user/hooks/useCurrentUser";
import { useActiveLang } from "../../domains/app/hooks/useActiveLang";
import { usePageLinks } from "../../domains/app/hooks/usePageLinks";
import { getItemTranslation } from "../../domains/app/utils/getItemTranslation";
import { usePlaylistVisits } from "../../domains/playlist/stores/playlistVisits";
import { getRoomLogoUrl } from "../../domains/room/utils/getRoomLogoUrl";
import { getRoomName } from "../../domains/room/hooks/useRoomName";
import { seedArticleDetailCache } from "../../features/article/hooks/useArticleData";
import {
  DesktopSearchPalette,
  type DesktopSearchPaletteGroup,
  type DesktopSearchPaletteItem,
} from "./DesktopSearchPalette";
import { roomLineSubtitle } from "../../domains/room/utils/roomContentCounts";

const paletteStateStore = memoryStore<"open" | "closed">("closed");
const paletteQueryStore = memoryStore("");

// The control that opened the palette, which gets the focus back on close.
// `null` when it was opened from the keyboard shortcut.
let returnFocusTo: HTMLElement | null = null;

// How many hosts are mounted. The desktop shell leaves the host out while the
// sign-in gate is up, and the palette must not open over it.
let mountedHosts = 0;

/**
 * ⌘K and the top bar's search trigger. `false` when no palette can open here,
 * so the caller can fall back to the `/search` page.
 */
function openDesktopSearchPalette(opener: HTMLElement | null): boolean {
  if (mountedHosts === 0) return false;
  returnFocusTo = opener;
  paletteStateStore.set("open");
  return true;
}

/** The debounce between a keystroke and the searches it starts. */
const SEARCH_DEBOUNCE_MS = 300;

const RECENT_COUNT = 3;
const JOINED_ROOM_COUNT = 3;
const TOPIC_RESULT_COUNT = 2;
const ROOM_RESULT_COUNT = 2;
const ARTICLE_RESULT_COUNT = 3;
const COLLECTION_RESULT_COUNT = 2;

/**
 * The desktop search palette, mounted once by the desktop shell and opened by
 * `openDesktopSearchPalette`. It centralises search on desktop: the reader's
 * recent items, topics and rooms while the field is empty, then an "Ask
 * JuisciGPT" action and the topics, rooms, articles and collections the query
 * matches.
 *
 * Each group comes from its own endpoint, the ones the `/search` page reads,
 * with a small limit: there is no multi-entity search on the API.
 */
/** Counts this host for `openDesktopSearchPalette` while it is mounted. */
function useRegisterPaletteHost(): void {
  useEffect(() => {
    mountedHosts += 1;
    return () => {
      mountedHosts -= 1;
    };
  }, []);
}

/** Whether the palette is on screen: Escape then belongs to it alone. */
function isDesktopSearchPaletteOpen(): boolean {
  return paletteStateStore.get() === "open";
}

function DesktopSearchPaletteHost(): React.JSX.Element | null {
  const [state] = useStore(paletteStateStore);
  useRegisterPaletteHost();
  if (state === "closed") return null;
  return <OpenPalette />;
}

function OpenPalette(): React.JSX.Element {
  const { t } = useTranslation();
  const history = useHistory();
  const { activeLang, contentLang } = useActiveLang();
  const { currentUser } = useCurrentUser();
  const { articleLink, publicPlaylistLink } = usePageLinks(null);
  const [query, setQuery] = useStore(paletteQueryStore);
  const trimmed = query.trim();
  const debounced = useDebouncedValue(trimmed, SEARCH_DEBOUNCE_MS);
  // The `/search` page's threshold, so "See all" never lands on a query it
  // would not run. "Ask JuisciGPT" is offered from the first character.
  const isSearching = debounced.length >= SEARCH_MIN_LENGTH;
  const isSignedIn = currentUser !== null;
  const playlistVisits = usePlaylistVisits(
    currentUser === null ? null : currentUser.id
  );

  // ── Empty field ───────────────────────────────────────────────────────
  const recentlyReadQuery = useApiQuery(
    "/webapp/private/feed/recently-read",
    isSignedIn && trimmed === ""
      ? { query: { limit: RECENT_COUNT, offset: 0 } }
      : false
  );
  const myTopicsQuery = useApiQuery(
    "/webapp/private/users/me/topics",
    isSignedIn && trimmed === "" ? {} : false
  );
  // Also read while searching: it says which matched rooms are joined.
  const joinedRoomsQuery = useApiQuery(
    "/webapp/private/rooms",
    isSignedIn ? {} : false
  );

  // ── Search ────────────────────────────────────────────────────────────
  const topicsQuery = useApiQuery(
    "/webapp/private/topics/search",
    isSignedIn && isSearching ? { query: { q: debounced } } : false
  );
  const roomsQuery = useApiQuery(
    "/webapp/public/rooms/search",
    isSearching
      ? {
          query: { search: debounced, limit: ROOM_RESULT_COUNT, offset: 0 },
        }
      : false
  );
  const articlesQuery = useApiQuery(
    "/webapp/public/articles/search",
    isSearching
      ? {
          query: {
            search: debounced,
            limit: ARTICLE_RESULT_COUNT,
            offset: 0,
            scoring: "relevant",
            adminPanel: false,
            impactFactorMoreThan: 0,
            meshResolver: true,
          },
        }
      : false
  );
  const collectionsQuery = useApiQuery(
    "/webapp/public/playlists/search",
    isSearching
      ? {
          query: {
            search: debounced,
            limit: COLLECTION_RESULT_COUNT,
            offset: 0,
            meshResolver: true,
          },
        }
      : false
  );

  // The analytics and the search history `/search` keeps: a settled query is
  // recorded, and its article count is reported once the search lands.
  const searchHistory = useSearchHistory();
  const tracking = useTrackSearchQuery();
  const settled = useRecordSettledSearch(
    isSearching ? debounced : "",
    searchHistory.addEntry
  );
  useTrackSearchSectionSettled({
    isLoading:
      debounced !== settled ||
      articlesQuery.state !== "success" ||
      articlesQuery.isFetching,
    fire: tracking.fireResultsSettled,
    term: settled,
    category: "articles",
    count:
      articlesQuery.state === "success" ? articlesQuery.body.meta.total : 0,
    scope: "global",
  });

  function close() {
    setQuery("");
    paletteStateStore.set("closed");
    const opener = returnFocusTo;
    returnFocusTo = null;
    if (opener !== null) opener.focus();
  }

  function go(path: string) {
    close();
    history.push(path);
  }

  // What opening each item does, by key, built beside the groups.
  const actions = new Map<string, () => void>();
  function item<T extends DesktopSearchPaletteItem>(
    entry: T,
    action: () => void
  ): T {
    actions.set(entry.key, action);
    return entry;
  }

  const joinedRooms =
    joinedRoomsQuery.state === "success" ? joinedRoomsQuery.body.rooms : [];
  const joinedIds = new Set(joinedRooms.map((room) => room.id));

  const groups: DesktopSearchPaletteGroup[] = [];

  if (trimmed === "") {
    const recentArticles =
      recentlyReadQuery.state === "success"
        ? recentlyReadQuery.body.docs.map((article) => {
            return {
              at: new Date(article.readingDate).getTime(),
              entry: item(
                {
                  kind: "article",
                  key: `recent-article-${article.id}`,
                  title: article.title,
                  journalName:
                    article.journal === null ? null : article.journal.name,
                  impactFactor:
                    article.journal === null ||
                    article.journal.impact_factor === 0
                      ? null
                      : article.journal.impact_factor,
                  date: formatArticleDate(article.publication_date, activeLang),
                },
                () => {
                  seedArticleDetailCache(article, null);
                  go(articleLink(article));
                }
              ),
            };
          })
        : [];
    const recentCollections = playlistVisits.map((visit) => ({
      at: visit.visitedAt,
      entry: item(
        {
          kind: "collection",
          key: `recent-collection-${visit.id}`,
          title: visit.title,
          meta: t("Collection · {{count}} articles", {
            count: visit.articleCount,
          }),
        },
        () => {
          go(
            visit.roomId === null
              ? publicPlaylistLink({ id: visit.id })
              : `/room/playlist/${visit.roomId}/${visit.id}`
          );
        }
      ),
    }));
    const recent = [...recentArticles, ...recentCollections]
      .sort((a, b) => b.at - a.at)
      .slice(0, RECENT_COUNT)
      .map((entry) => entry.entry);
    // Held until the server's list lands: merging it in later would reorder the
    // rows, and the highlighted one, under the reader.
    if (recentlyReadQuery.state !== "pending" && recent.length > 0)
      groups.push({
        status: "ready",
        key: "recent",
        title: t("Recently viewed"),
        items: recent,
      });

    if (myTopicsQuery.state === "success" && myTopicsQuery.body.length > 0)
      groups.push({
        status: "ready",
        key: "my-topics",
        title: t("Your topics"),
        items: myTopicsQuery.body.map((topic) => {
          const name = getItemTranslation(topic);
          return item(
            { kind: "topic-chip", key: `my-topic-${topic.uid}`, name },
            () => {
              tracking.recordIntent(name, "suggestion");
              setQuery(name);
            }
          );
        }),
      });

    if (joinedRooms.length > 0)
      groups.push({
        status: "ready",
        key: "my-rooms",
        title: t("Your rooms"),
        items: joinedRooms.slice(0, JOINED_ROOM_COUNT).map((room) => {
          return item(
            {
              kind: "room",
              key: `my-room-${room.id}`,
              name: getRoomName(room, activeLang),
              logoUrl: getRoomLogoUrl(room),
              meta: roomLineSubtitle(room, contentLang, t),
            },
            () => {
              go(`/room/discovery/${room.id}`);
            }
          );
        }),
      });
  } else {
    // Results only for the query in the field: while a new term settles, the
    // previous term's rows would open something the reader no longer asks for.
    const resultsMatchField = trimmed === debounced;
    // A search group the field is about to fill: the term is long enough to be
    // searched, and its rows are not in yet, because the term is still
    // settling or its own request has not answered. It holds its place with
    // the silhouette of its rows rather than appearing later under the reader.
    // A failed search is not loading: its group stays out, as an empty one does.
    const fieldSearches = trimmed.length >= SEARCH_MIN_LENGTH;
    function awaits(search: {
      state: "idle" | "pending" | "error" | "success";
    }): boolean {
      return (
        fieldSearches && (!resultsMatchField || search.state === "pending")
      );
    }
    groups.push({
      status: "ready",
      key: "ask",
      title: null,
      items: [
        item({ kind: "ask", key: "ask", question: trimmed }, () => {
          close();
          history.push("/gpt/chat/new", { initialQuestion: trimmed });
        }),
      ],
    });

    if (isSignedIn && awaits(topicsQuery))
      groups.push({
        status: "loading",
        key: "topics",
        title: t("Topics"),
        shape: "entry",
        rows: TOPIC_RESULT_COUNT,
      });
    else if (
      resultsMatchField &&
      topicsQuery.state === "success" &&
      topicsQuery.body.length > 0
    )
      groups.push({
        status: "ready",
        key: "topics",
        title: t("Topics"),
        items: topicsQuery.body.slice(0, TOPIC_RESULT_COUNT).map((topic) => {
          const name = getItemTranslation(topic);
          return item(
            {
              kind: "topic",
              key: `topic-${topic.uid}`,
              name,
              meta: t("Topic"),
            },
            () => {
              tracking.recordIntent(name, "suggestion");
              setQuery(name);
            }
          );
        }),
      });

    if (awaits(roomsQuery))
      groups.push({
        status: "loading",
        key: "rooms",
        title: t("Rooms"),
        shape: "entry",
        rows: ROOM_RESULT_COUNT,
      });
    else if (
      resultsMatchField &&
      roomsQuery.state === "success" &&
      roomsQuery.body.docs.length > 0
    )
      groups.push({
        status: "ready",
        key: "rooms",
        title: t("Rooms"),
        items: roomsQuery.body.docs.map((room) =>
          item(
            {
              kind: "room",
              key: `room-${room.id}`,
              name: getRoomName(room, activeLang),
              logoUrl: getRoomLogoUrl(room),
              meta: joinedIds.has(room.id)
                ? t("Room · {{organisation}} · joined", {
                    organisation: room.organisationModel.name,
                  })
                : t("Room · {{organisation}}", {
                    organisation: room.organisationModel.name,
                  }),
            },
            () => {
              go(`/room/discovery/${room.id}`);
            }
          )
        ),
      });

    if (awaits(articlesQuery))
      groups.push({
        status: "loading",
        key: "articles",
        title: t("Articles"),
        shape: "article",
        rows: ARTICLE_RESULT_COUNT,
      });
    else if (
      resultsMatchField &&
      articlesQuery.state === "success" &&
      articlesQuery.body.docs.length > 0
    ) {
      const total = articlesQuery.body.meta.total;
      groups.push({
        status: "ready",
        key: "articles",
        title: t("Articles"),
        items: [
          ...articlesQuery.body.docs.map((article) =>
            item(
              {
                kind: "article",
                key: `article-${article.id}`,
                title: article.title,
                journalName:
                  article.journal === null ? null : article.journal.name,
                // The search's article cards carry no impact factor.
                impactFactor: null,
                date: formatArticleDate(article.publication_date, activeLang),
              },
              () => {
                go(articleLink(article));
              }
            )
          ),
          ...(total > articlesQuery.body.docs.length
            ? [
                item(
                  {
                    kind: "see-all",
                    key: "see-all-articles",
                    label: t("See all {{count}} articles", { count: total }),
                  },
                  () => {
                    close();
                    history.push(
                      `/search?${new URLSearchParams({ value: debounced, category: "articles" }).toString()}`,
                      { paletteTrackedTerm: settled }
                    );
                  }
                ),
              ]
            : []),
        ],
      });
    }

    if (awaits(collectionsQuery))
      groups.push({
        status: "loading",
        key: "collections",
        title: t("Collections"),
        shape: "entry",
        rows: COLLECTION_RESULT_COUNT,
      });
    else if (
      resultsMatchField &&
      collectionsQuery.state === "success" &&
      collectionsQuery.body.docs.length > 0
    )
      groups.push({
        status: "ready",
        key: "collections",
        title: t("Collections"),
        items: collectionsQuery.body.docs.map((playlist) =>
          item(
            {
              kind: "collection",
              key: `collection-${playlist.id}`,
              title: playlist.title,
              meta: t("Collection · {{count}} articles", {
                count: playlist.articleCount,
              }),
            },
            () => {
              go(publicPlaylistLink(playlist));
            }
          )
        ),
      });
  }

  const isLoading =
    trimmed !== debounced ||
    (isSearching &&
      [topicsQuery, roomsQuery, articlesQuery, collectionsQuery].some(
        (search) => search.isFetching
      ));

  return (
    <DesktopSearchPalette
      query={query}
      onQueryChange={setQuery}
      groups={groups}
      isLoading={isLoading}
      onSelect={(selected) => {
        const action = actions.get(selected.key);
        if (action === undefined)
          throw new Error(`should not happen: no action for ${selected.key}`);
        action();
      }}
      onClose={close}
    />
  );
}

export {
  DesktopSearchPaletteHost,
  isDesktopSearchPaletteOpen,
  openDesktopSearchPalette,
};
